import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { STATUS, PRIORITY, daysUntil } from "../lib/utils";
import TaskModal from "../components/TaskModal";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DAYS_ES = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];

export default function CalendarPage() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [t, u, a, p] = await Promise.all([api.get("/tasks"), api.get("/users"), api.get("/areas"), api.get("/projects")]);
    setTasks(t.data); setUsers(u.data); setAreas(a.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const grid = useMemo(() => {
    const year = cursor.getFullYear(); const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startWeekday = (first.getDay() + 6) % 7; // Mon=0
    const lastDay = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i=0; i<startWeekday; i++) cells.push(null);
    for (let d=1; d<=lastDay; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const tasksByDate = useMemo(() => {
    const m = {};
    tasks.forEach(t => {
      if (!t.due_date) return;
      m[t.due_date] = m[t.due_date] || [];
      m[t.due_date].push(t);
    });
    return m;
  }, [tasks]);

  const iso = (d) => d ? d.toISOString().slice(0,10) : "";

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Calendario</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Tareas por fecha de vencimiento</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1))} className="p-2 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-heading font-semibold text-lg min-w-[180px] text-center">
            {MONTHS_ES[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1))} className="p-2 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {DAYS_ES.map(d => <div key={d} className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-3 py-2 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            const key = iso(d);
            const list = key ? (tasksByDate[key] || []) : [];
            const isToday = d && d.toDateString() === today.toDateString();
            return (
              <div key={i} className={`min-h-[100px] p-2 border-b border-r border-slate-100 ${!d ? "bg-slate-50/50" : ""}`}>
                {d && (
                  <div className={`text-xs font-semibold mb-1 ${isToday ? "text-crejpp" : "text-slate-500"}`}>
                    {d.getDate()}
                    {isToday && <span className="ml-1 text-[10px] uppercase">Hoy</span>}
                  </div>
                )}
                <div className="space-y-1">
                  {list.slice(0,3).map(t => {
                    const days = daysUntil(t.due_date);
                    const overdue = t.status !== "cumplido" && days < 0;
                    const soon = t.status !== "cumplido" && days >=0 && days <=3;
                    const s = STATUS[t.status];
                    return (
                      <button key={t.id} onClick={() => { setCurrent(t); setOpen(true); }} className={`w-full text-left text-[11px] px-1.5 py-1 rounded truncate ${overdue ? "bg-red-50 text-crejpp" : soon ? "bg-amber-50 text-amber-800" : `${s.bg} ${s.text}`}`}>
                        {t.title}
                      </button>
                    );
                  })}
                  {list.length > 3 && <div className="text-[10px] text-slate-500">+{list.length-3} más</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TaskModal open={open} onClose={() => setOpen(false)} task={current} onSaved={load} areas={areas} users={users} projects={projects} />
    </div>
  );
}
